import { EtherscanLink } from '@/components';
import {
  Card,
  CurrencyValue,
  Flex,
  Heading,
  PercentageValue,
  RelativeDateTime,
  Text,
  WalletAddress
} from '@blockanalitica/ui';
import _ from 'lodash';

type DataResponse = {
  assets: string;
  assets_usd: string;
  network: string;
  address: string;
  wallet_address: string;
  symbol: string;
  name: string;
  principal: string;
  profit_total: string;
  pending_amount?: string;
  cooldown_end?: number;
  asset_symbol: string;
  asset_address: string;
  asset_name: string;
  protocol: string;
  price: string;
  wallet_label?: string;
  idle?: string;
  rate_1d_avg: string | number | null;
  rate_7d_avg: string | number | null;
  rate: string | number | null;
  last_event_datetime: string;
  shares: string;
  token_total_supply: string;
  aum: string;
};

export default function InfoSection({ data }: { data: DataResponse }) {
  return (
    <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
      <Card gap="m">
        <Heading size="4">Protocol Information</Heading>
        <Flex flexDirection="column" gap="xs">
          <Flex gap="s" justifyContent="space-between">
            <Text>Protocol:</Text>
            <Text>{_.capitalize(data.protocol)}</Text>
          </Flex>
          <Flex gap="s" justifyContent="space-between">
            <Text>Network:</Text>
            <Text>{_.capitalize(data.network)}</Text>
          </Flex>
          <Flex gap="s" justifyContent="space-between">
            <Text>Asset:</Text>
            <Flex gap="xs">
              <Text color="muted">({data.asset_name})</Text>
              <Text>{data.asset_symbol}</Text>
              <EtherscanLink
                value={data.asset_address}
                network={data.network}
                type="address"
              />
            </Flex>
          </Flex>
          {data.symbol && (
            <Flex gap="s" justifyContent="space-between">
              <Text>Token:</Text>
              <Flex gap="xs">
                <Text color="muted">({data.name})</Text>
                <Text>{data.symbol}</Text>
                <EtherscanLink
                  value={data.address}
                  network={data.network}
                  type="address"
                />
              </Flex>
            </Flex>
          )}
          <Flex gap="s" justifyContent="space-between">
            <Text>Token Price:</Text>
            <CurrencyValue
              value={data.price}
              options={{
                currency: 'USD'
              }}
            />
          </Flex>
          {data.pending_amount && data.cooldown_end && (
            <>
              <Flex gap="s" justifyContent="space-between">
                <Text>Pending amount::</Text>
                <Flex gap="xs">
                  <CurrencyValue value={data.pending_amount} />
                </Flex>
              </Flex>
              <Flex gap="s" justifyContent="space-between">
                <Text>Cooldown end:</Text>
                <Flex gap="xs">
                  <RelativeDateTime value={data.cooldown_end} />
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      </Card>

      <Card gap="m">
        <Heading size="4">Allocation Information</Heading>

        <Flex flexDirection="column" gap="xs">
          <Flex gap="s" justifyContent="space-between">
            <Text>Allocator:</Text>
            <Flex gap="xs">
              {data.wallet_label && (
                <Text color="muted">({data.wallet_label})</Text>
              )}
              <WalletAddress
                address={data.wallet_address}
                options={{
                  showBlockie: false
                }}
              />
              <EtherscanLink
                value={data.wallet_address}
                network={data.network}
                type="address"
              />
            </Flex>
          </Flex>
          <Flex flexDirection="row" gap="s" justifyContent="space-between">
            <Text>Deployed:</Text>
            <CurrencyValue value={Number(data['assets'])} />
          </Flex>
          <Flex flexDirection="row" gap="s" justifyContent="space-between">
            <Text>Idle Liquidity:</Text>
            <CurrencyValue
              value={Number(data['assets']) - Number(data['aum'])}
            />
          </Flex>
          <Flex flexDirection="row" gap="s" justifyContent="space-between">
            <Text>Utilization:</Text>
            <PercentageValue
              value={Number(data['aum']) / Number(data['assets'])}
            />
          </Flex>
          <Flex gap="s" justifyContent="space-between">
            <Text>Principal:</Text>
            <CurrencyValue value={data.principal} />
          </Flex>
          <Flex gap="s" justifyContent="space-between">
            <Text>Curent APY:</Text>
            <PercentageValue value={data.rate} />
          </Flex>
          {data.token_total_supply && (
            <Flex gap="s" justifyContent="space-between">
              <Text>Share of Total Supply:</Text>
              <PercentageValue
                value={Number(data.shares) / Number(data.token_total_supply)}
              />
            </Flex>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
